import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { LockOutlined } from '@material-ui/icons';
import {
  Button,
  Container,
  CssBaseline,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import Lottie from "react-lottie";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import SecurityLottie from "../../assets/lottie/security.json";
import toastError from "../../errors/toastError";

import iconLight from "../../assets/miscPng/icon.png";
import iconDark from "../../assets/miscPng/icon-dark.png";

import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";
import LottieUtils from "../../utils/classes/LottieUtils";

const useStyles = makeStyles((theme) => ({
  //  ****************
  //  ** Containers **
  //  ****************
  passwordResetValidationContainer: {
    width: "100vw",
    height: "90vh",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
    marginTop: "5vh",
    userSelect: "none",
    name: theme.name,
    color: theme.palette.lottie.plainBackground,
  },

  imgContainer: {
    backgroundColor: theme.palette.lottie.plainBackground,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "10px 0px 0px 10px",
    [theme.breakpoints.down("sm")]: { display: "none", },
  },

  passwordResetValidationForm: {
    backgroundColor: theme.palette.lottie.secondaryPlainBackground,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0px 10px 10px 0px",
  },

  //  ********************
  //  ** Form Container **
  //  ********************
  avatar: {
    margin: theme.spacing(0),
    width: "100px",
    height: "auto",
  },

  textField: {
    width: "80%",

    "& label.MuiFormLabel-root": { color: theme.palette.lottie.plainBackground, },

    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: theme.palette.lottie.plainBackground, },
      "&:hover fieldset": { borderColor: theme.palette.lottie.plainBackground, },
      "&.Mui-focused fieldset": { borderColor: "red", },
    },
  },

  textFieldInput: { color: theme.palette.lottie.plainBackground, },

  floatingButton: {
    margin: theme.spacing(3, 0, 2),
    transition: "transform 0.30s",
    "&:hover": { transform: "translateY(-5px)", },
  },

  secondaryButton: { color: theme.palette.lottie.plainBackground, },
}));

const PasswordResetDefinition = () => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");

  const currentIcon = localStorage.getItem("theme") ? iconDark : iconLight;
  const hashParam = new URLSearchParams(window.location.search).get("hash");

  

  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => { document.title = "BestZap"; }, []);

  useEffect(() => {
    const checkResetPasswordRequisition = async () => {
      if (!hashParam) {
        history.push(DefaultValuesUtils.ROUTES[106]);
        return;
      }
  
      try {
        const { data } = await api.get("/passwordReset", { params: { hash: hashParam } });
        
        if (data.type === "warning") {
          toast.info(i18n.t(`backendErrors.${data.msg}`));
          history.push(DefaultValuesUtils.ROUTES[106]);
        }
      } catch (exception) {
        console.log("Password Reset Validation Exception:", exception);
        toastError(exception);
        history.push(DefaultValuesUtils.ROUTES[106]);
      }
    };

    checkResetPasswordRequisition();
  }, [hashParam, history]); 



  //  ***************
  //  ** Functions **
  //  ***************
  const handleChangeInput = (event, isPasswordConfirmation) => {
    if (isPasswordConfirmation) setPasswordConfirmation(event.target.value);
    else setPassword(event.target.value);
  };

  const handleKeyDownInput = (event, isPasswordConfirmation) => {
    if (event.key === "Enter" && !isPasswordConfirmation) document.getElementById("passwordConfirmation").focus();
    else if (event.key === "Enter") document.getElementById("confirmButton").click();
  };

  const handleValidatePassword = async () => {
    if (password.length < 5) {
      toast.info(i18n.t("passwordResetDefinition.validations.passwordLength"));
      return;
    }

    if (password !== passwordConfirmation) {
      toast.info(i18n.t("passwordResetDefinition.validations.passwordMatch"));
      return;
    }

    try {
      setLoading(true);

      const { data } = await api.put("/passwordReset/updatePassword", { hash: hashParam, password });
      
      if (data.type === "warning") {
        toast.info(i18n.t(`backendErrors.${data.msg}`));
        history.push(DefaultValuesUtils.ROUTES[106]);
      }

      else {
        toast.success(i18n.t("passwordResetDefinition.validations.success"));
        history.push(DefaultValuesUtils.ROUTES[102]);
      }

    } catch (exception) {
      console.log("Password Reset Definition - Handle Validate Password Exception:", exception);
      toastError(exception);
    } finally {
      setLoading(false);
    }
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <Container className={classes.passwordResetValidationContainer} component="main">
      <CssBaseline />

      <div className={classes.imgContainer}>
        <Lottie
          options={{ ...LottieUtils.ANIMATION_PROPERTIES, animationData: SecurityLottie }}
          isClickToPauseDisabled={true}
          style={{ width: "80.00%", height: "80.00%", }}
        />
      </div>

      <div className={classes.passwordResetValidationForm}>
        <img className={classes.avatar} src={currentIcon} alt="BestZap Logo" />

        <Typography component="h1" variant="h3">
          {i18n.t("passwordReset.title")}
        </Typography>

        <Typography component="h2" variant="h6">
          <br />
          {i18n.t("passwordResetDefinition.subtitle")}
          <br /><br />
        </Typography>

        <TextField
          className={classes.textField}
          label={i18n.t("passwordResetDefinition.form.password")}
          autoComplete="off"
          variant="outlined"
          margin="normal"
          
          id="password"
          name="password"
          type="password"
          value={password}
          onChange={(event) => handleChangeInput(event, false)}
          onKeyDown={(event) => handleKeyDownInput(event, false)}

          InputProps={{
            startAdornment: (<InputAdornment position="start"><LockOutlined /></InputAdornment>),
            className: classes.textFieldInput
          }}

          autoFocus
          required
        />


        <TextField
          className={classes.textField}
          label={i18n.t("passwordResetDefinition.form.passwordConfirmation")}
          autoComplete="off"
          variant="outlined"
          margin="normal"

          id="passwordConfirmation"
          name="passwordConfirmation"
          type="password"
          value={passwordConfirmation}
          onChange={(event) => handleChangeInput(event, true)}
          onKeyDown={(event) => handleKeyDownInput(event, true)}

          InputProps={{
            startAdornment: (<InputAdornment position="start"><LockOutlined /></InputAdornment>),
            className: classes.textFieldInput
          }}

          required
        />

        <Button
          id="confirmButton"
          variant="contained"
          color="primary"
          style={{ width: "80%" }}
          disabled={loading}
          className={`${classes.floatingButton} ${classes.secondaryButton}`}
          onClick={handleValidatePassword}
        >
          {i18n.t("passwordResetDefinition.buttons.confirm")}
        </Button>
      </div>
    </Container>
  );
};

export default PasswordResetDefinition;
